import React, { useState } from 'react';

import { useSnackbar } from 'notistack';
import { useParams } from 'react-router-dom';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  IconButton,
  Stack,
  Step,
  StepButton,
  Stepper,
  Tooltip,
  Typography,
} from '@mui/material';

import PageHeader from '~/components/Common/Elements/PageHeader';

import { useGetBundle, useUpdateBundle } from '~/hooks/apis/bundle/useBundles';

import { BUNDLE_STATUS_LABELS, Bundle } from '~/types/bundles/bundle';

import BundleDeparturesStep from '../../Bundles/BundleDeparturesStep';
import BundleItinerary from '../../Bundles/BundleItinerary';
import BundleOverview from '../../Bundles/BundleOverview';
import RenameBundleDialog from '../../Bundles/RenameBundleDialog';
import useBundleTourSpan from '../../Bundles/useBundleTourSpan';
import { BUNDLES_PATH } from '../../nativeTourModel';

const BUILD_STEP = 0;
const DEPARTURES_STEP = 1;

// Content is edited on the LTLE, which approval creates; pricing comes after that.
const LATER_STEPS = [
  { label: 'Content', note: 'After approval' },
  { label: 'Prices', note: 'Coming soon' },
];

function BundleSteps({ bundle }: { bundle: Bundle }) {
  const { tour } = useBundleTourSpan(bundle);
  const hotels = bundle.components.filter((component) => component.product_type === 'flashAccommodation');
  const departures = bundle.departure_dates;
  const isBuilt = !!tour && hotels.length > 0;
  const [activeStep, setActiveStep] = useState(isBuilt ? DEPARTURES_STEP : BUILD_STEP);

  const departuresNote = departures.length === 0 ? 'None selected' : `${departures.length} selected`;

  return (
    <Stack spacing={3}>
      <Card variant="outlined" sx={{ px: 2, py: 2.5 }}>
        <Stepper nonLinear activeStep={activeStep}>
          <Step completed={isBuilt}>
            <StepButton
              onClick={() => setActiveStep(BUILD_STEP)}
              optional={<Typography variant="caption">Tour and hotel</Typography>}
            >
              Build
            </StepButton>
          </Step>
          <Step completed={departures.length > 0}>
            <StepButton
              onClick={() => setActiveStep(DEPARTURES_STEP)}
              optional={<Typography variant="caption">{departuresNote}</Typography>}
            >
              Departures
            </StepButton>
          </Step>
          {LATER_STEPS.map(({ label, note }) => (
            <Step key={label} disabled>
              <StepButton optional={<Typography variant="caption">{note}</Typography>}>{label}</StepButton>
            </Step>
          ))}
        </Stepper>
      </Card>

      <Grid container spacing={3} alignItems="flex-start">
        <Grid size={{ xs: 12, md: 8 }}>
          {activeStep === BUILD_STEP && (
            <Stack spacing={4}>
              <BundleItinerary bundle={bundle} />
              <Box display="flex" justifyContent="flex-end">
                <Button
                  variant="contained"
                  endIcon={<ArrowForwardIcon />}
                  onClick={() => setActiveStep(DEPARTURES_STEP)}
                  disabled={!tour}
                >
                  Continue to departures
                </Button>
              </Box>
            </Stack>
          )}
          {activeStep === DEPARTURES_STEP && (
            <Stack spacing={4}>
              <BundleDeparturesStep bundle={bundle} />
              <Box>
                <Button startIcon={<ArrowBackIcon />} onClick={() => setActiveStep(BUILD_STEP)}>
                  Back to build
                </Button>
              </Box>
            </Stack>
          )}
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <BundleOverview bundle={bundle} />
        </Grid>
      </Grid>
    </Stack>
  );
}

export default function TourBundlePage() {
  const { bundle_id: bundleId } = useParams<{ bundle_id: string }>();
  const { enqueueSnackbar } = useSnackbar();

  const [isRenameOpen, setIsRenameOpen] = useState(false);
  const [isApproveOpen, setIsApproveOpen] = useState(false);

  const { data: bundle, isLoading, error } = useGetBundle(bundleId);
  const updateBundle = useUpdateBundle(bundleId);

  const approveBlocker = !bundle
    ? undefined
    : bundle.components.length === 0
      ? 'Add a tour before approving'
      : bundle.departure_dates.length === 0
        ? 'Choose departure dates before approving'
        : undefined;

  const handleStatusChange = async (status: 'draft' | 'approved') => {
    try {
      await updateBundle.mutateAsync({ status });
      setIsApproveOpen(false);
      enqueueSnackbar(`Bundle ${status === 'approved' ? 'approved' : 'moved back to draft'}`, { variant: 'success' });
    } catch (err) {
      enqueueSnackbar(err.message, { variant: 'error' });
    }
  };

  return (
    <Container maxWidth="xl">
      <PageHeader
        title={bundle?.name ?? 'Bundle'}
        backButton={BUNDLES_PATH}
        titleActions={
          bundle && (
            <Stack direction="row" spacing={1} alignItems="center">
              <Tooltip title="Rename">
                <IconButton size="small" aria-label="Rename bundle" onClick={() => setIsRenameOpen(true)}>
                  <EditOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Chip
                size="small"
                label={BUNDLE_STATUS_LABELS[bundle.status]}
                color={bundle.status === 'approved' ? 'success' : 'default'}
              />
            </Stack>
          )
        }
      >
        <Box pb={1}>
          {bundle?.status === 'draft' && (
            <Tooltip title={approveBlocker ?? ''}>
              {/* Disabled buttons fire no events, so the tooltip needs a wrapper. */}
              <span>
                <Button
                  variant="contained"
                  onClick={() => setIsApproveOpen(true)}
                  disabled={!!approveBlocker || updateBundle.isPending}
                >
                  Approve
                </Button>
              </span>
            </Tooltip>
          )}
          {bundle?.status === 'approved' && (
            <Button onClick={() => handleStatusChange('draft')} disabled={updateBundle.isPending}>
              Move back to draft
            </Button>
          )}
        </Box>
      </PageHeader>

      {isLoading && (
        <Box p={4} display="flex" justifyContent="center">
          <CircularProgress />
        </Box>
      )}

      {!!error && <Alert severity="error">{error.message}</Alert>}

      {!!bundle && (
        <>
          <BundleSteps bundle={bundle} />
          <RenameBundleDialog bundle={bundle} open={isRenameOpen} onClose={() => setIsRenameOpen(false)} />
        </>
      )}

      <Dialog open={isApproveOpen} onClose={() => setIsApproveOpen(false)}>
        <DialogTitle>Approve bundle?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Components and departure dates can't be changed once the bundle is approved. Price adjustments still can.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsApproveOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={() => handleStatusChange('approved')} disabled={updateBundle.isPending}>
            Approve
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
